/**
 * UX analytics — click positions + per-page dwell times.
 *
 * Gated by the `uxAnalytics` setting (default `REACT_APP_UX_ANALYTICS`). When
 * on, events are POSTed in batches to `REACT_APP_ANALYTICS_URL`. Events are
 * held in a localStorage ring buffer until a POST succeeds, so nothing is lost
 * when the endpoint is offline/unset — the buffer flushes on the next success
 * or the next app load. If no URL is configured the events simply buffer
 * locally (one warning is logged).
 *
 * No PII is collected: only coordinates, the route, and the label of the
 * nearest button/link.
 */
import { getBool } from '../settings';

const ANALYTICS_URL = process.env.REACT_APP_ANALYTICS_URL || '';
const STORAGE_KEY = 'rush-ux-analytics-v1';
const MAX_BUFFER = 1000;
const FLUSH_INTERVAL_MS = 15_000;
const FLUSH_BATCH = 50;
const RECENT_CAP = 50;

export type ClickEvent = {
  type: 'click';
  t: number;
  route: string;
  x: number;
  y: number;
  vw: number;
  vh: number;
  tag: string;
  label?: string;
};

export type DwellEvent = {
  type: 'dwell';
  route: string;
  enterT: number;
  exitT: number;
  durationMs: number;
};

export type UxEvent = ClickEvent | DwellEvent;

export type FlushResult = { ok: boolean; t: number; status?: number; error?: string };

export const analyticsEnabled = (): boolean => getBool('uxAnalytics');

// ── buffers ──────────────────────────────────────────────────────────────────
// `pending` is the durable send queue (persisted). `recentLog` is a small
// display-only ring of the latest events (kept even after they're sent).
let pending: UxEvent[] = loadBuffer();
const recentLog: UxEvent[] = [];
let warnedNoUrl = false;
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;
let lastFlush: FlushResult | null = null;

function loadBuffer (): UxEvent[] {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr.slice(-MAX_BUFFER) as UxEvent[]) : [];
  } catch {
    return [];
  }
}

function persist (): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pending.slice(-MAX_BUFFER)));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Enqueue an event (no-op unless analytics is enabled). */
export function record (ev: UxEvent): void {
  if (!analyticsEnabled()) return;
  pending.push(ev);
  if (pending.length > MAX_BUFFER) pending = pending.slice(-MAX_BUFFER);
  recentLog.push(ev);
  if (recentLog.length > RECENT_CAP) recentLog.shift();
  persist();
}

/** POST buffered events in batches; drop them only once the server accepts. */
export async function flush (): Promise<void> {
  if (!analyticsEnabled() || pending.length === 0) return;
  if (!ANALYTICS_URL) {
    if (!warnedNoUrl) {
      // eslint-disable-next-line no-console
      console.warn('[ux-analytics] REACT_APP_ANALYTICS_URL is not set — events are buffered locally only.');
      warnedNoUrl = true;
    }
    return;
  }
  const batch = pending.slice(0, FLUSH_BATCH);
  try {
    const res = await fetch(ANALYTICS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch, sentAt: Date.now() }),
      keepalive: true,
    });
    if (!res.ok) {
      lastFlush = { ok: false, t: Date.now(), status: res.status };
      return;
    }
    pending = pending.slice(batch.length);
    persist();
    lastFlush = { ok: true, t: Date.now(), status: res.status };
    if (pending.length > 0) void flush(); // drain the rest
  } catch (e) {
    lastFlush = { ok: false, t: Date.now(), error: e instanceof Error ? e.message : String(e) };
  }
}

/** Best-effort synchronous send on page hide, via sendBeacon. */
function flushBeacon (): void {
  if (!analyticsEnabled() || !ANALYTICS_URL || pending.length === 0) return;
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  try {
    const body = JSON.stringify({ events: pending.slice(-MAX_BUFFER), sentAt: Date.now() });
    const ok = navigator.sendBeacon(ANALYTICS_URL, new Blob([body], { type: 'application/json' }));
    if (ok) {
      pending = [];
      persist();
    }
  } catch {
    /* ignore */
  }
}

/** Start periodic flushing + page-hide beacon. Safe to call multiple times. */
export function startAnalytics (): void {
  if (started || !analyticsEnabled()) return;
  if (typeof window === 'undefined') return;
  started = true;
  void flush(); // drain anything buffered from a previous session
  timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', flushBeacon);
}

/** For tests / teardown. */
export function stopAnalytics (): void {
  if (timer) clearInterval(timer);
  timer = null;
  started = false;
  if (typeof window !== 'undefined') window.removeEventListener('pagehide', flushBeacon);
}

// ── admin helpers ─────────────────────────────────────────────────────────────
export function analyticsStatus () {
  return {
    enabled: analyticsEnabled(),
    url: ANALYTICS_URL,
    buffered: pending.length,
    lastFlush,
    recent: [...recentLog].reverse(),
  };
}

export function flushNow (): Promise<void> {
  return flush();
}

export function clearBuffer (): void {
  pending = [];
  recentLog.length = 0;
  persist();
}
